import React, { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import ConfirmModal from "../modals/ConfirmModal";
import ActivateAccountModal from "../modals/ActivateAccountModal";
import { useMessage } from "../../context/MessageContext";
import {
  activePayloadApi,
  deleteApi,
  deletePayloadApi,
  getApi,
  postApi,
  putApi,
} from "../../apis/api";
import { RootState } from "../../redux/store";
import { AGENT_RATE_NAME, CUSTOMER_RATE_NAME } from "../../utils/authUtils";
import {
  GENERAL_ERROR_MESSAGE,
  REPORT_DOWNLOAD_SUCCESS,
  REPORT_FILE_GENERATING_MESSAGE,
  TABLE_MESSAGES,
} from "../../utils/message";
import { setLocationData } from "../../redux/reducer/locationSlice/locationSlice";
import useOutsideClick from "../../hooks/useOutsideClick";
import ChipsCell from "./ChipsCell";
import DropdownModal, {
  AccordionField,
  AccordionItem,
  ModalTableConfig,
} from "../modals/DropdownModal";
import httpService from "../../apis/axios-config/http-service";
import { GET_REPORT_URL } from "../../apis/end-points";

import {
  ActiveIcon,
  ArrowDownLightIcon,
  ArrowUpLightIcon,
  BlueViewIcon,
  DeleteIcon,
  EditUserIcon,
  GreenDownloadIcon,
  NavigateIcon,
  ThreeDotIcon,
  UnactiveIcon,
  FileIcon,
  OrangeFileIcon,
  BlueFileIcon,
  DisabledActive,
  DisabledInactive,
  ViewDetailsIcon,
  ExternalLink,
  CheckIconIcon,
  CrossIconCircle,
  ApproveIcon,
  RejectIcon,
  ResendIcon,
} from "../../assets";


interface ColumnTypes {
  key: string;
  header: string;
  isSortable: boolean;
  className?: string;
  isActive?: boolean;
  handleActive?: boolean;
  upperCase?: boolean;
  dolloar?: boolean;
  checkbox?: boolean;
  checkboxId?: string;
  colorPallete?: boolean;
  isHoverable?: boolean;
  isDownloadIcon?: boolean;
  columnStyle?: React.CSSProperties;
  onCellClick?: (row: any) => void;
  externalLinkConfig?: { getUrl: (row: any) => string };
  // opt-in per column: renders an "open in new tab" icon next to a `simpleLink` cell.
  // defaults to false so other link columns are unaffected.
  showExternalLink?: boolean;
  maxChips?: number;
  // opt-in per column: renders a second, muted line beneath a `profileLink`
  // cell — e.g. the Direct Customer order number under a Dickerson order
  // number. Only drawn when the row holds a value, so columns that do not set
  // subKey render exactly as before.
  subKey?: string;
}

interface ActionConfigTypes {
  apiUrl: string;
  urlID: string;
  tenantIdUrl?: string;
  successMessage?: string;
  heading?: string;
  para?: string;
  extra?: string;
  rowName?: string;
  secondRowName?: string;
  onboardedMessage?: string;
  deleteByPayload?: boolean;
  activeByPayload?: boolean;
  deleteByPayloadKeys?: string[];
  activeByPayloadKeys?: string;
  activePayloadKeys?: string[];
  payloadKeys?: any;
  changeStateToView?: (hubStep: number, data: any) => void;
  notCheckedOnboarded?: boolean;
  extendedParams?: string[];
  deleteByPutApi?: boolean;
  staticContent?: string;
}

interface NavigateConfigTypes {
  apiUrl: string;
  urlID: string;
  stateData?: boolean;
  changeStateToEdit?: (hubStep: number, data: any) => void;
  onClick?: (data: any) => void;
  isEditWithoutLink?: boolean;
  condition?: (row: any) => boolean;
}

interface InvoiceConfigTypes {
  changeStateToEdit?: (data: any) => void;
}

/**
 * Config for the "activate immediately" shortcut offered inside the
 * resend-invitation popup for rows that have not completed onboarding.
 * Supplying it is what enables the section — omitting it (the default) keeps
 * the popup exactly as it is today.
 */
export interface ActivateAccountConfigTypes {
  /** Endpoint that sets the password and activates the account; the record id
   * from `urlID` is appended to it. */
  apiUrl: string;
  /** Row key holding the record id appended to `apiUrl`. */
  urlID: string;
  /** Row key holding the invited email — pre-filled and non-editable. */
  emailKey: string;
  /** Activate popup heading, e.g. "Activate Customer". */
  title: string;
  /** Note shown in the resend-invitation popup above the Activate button. */
  noteMessage: string;
  successMessage: string;
  errorMessage: string;
}

interface AddConfigTypes {
  heading: string;
  confirmationText: string;
  successMessage: string;
  apiUrl: string;
  urlID: string;
  payloadKeys: any;
  viewUrl: string;
  viewUrlID: string;
}
interface DropdownItemTypes {
  name: string;
  icon?: React.ReactElement;
  apiUrl: string;
  serialNumber: string;
  accordionFields?: AccordionField;
  tableConfig?: ModalTableConfig;
  mappingFunction?: (data: any) => AccordionItem[];
  downloadInvoice?: {
    apiUrl: string;
    fileName: string;
  };
  navigateItem?: {
    path: string;
    isCheckNavigateByKey: string;
    showErrorMessage: string;
    urlParamKey: string;
  };
  /**
   * Optional per-row override of this dropdown item's config. Lets a single
   * menu entry behave differently depending on the row it was opened from
   * (e.g. invoices set to EDI use a different history API and layout).
   * Returning `undefined` keeps the base config untouched.
   */
  resolveByRow?: (row: any) => Partial<DropdownItemTypes> | undefined;
}

interface ManageConfigTypes {
  dropdownName: DropdownItemTypes[];
}

interface TablePropsTypes {
  columns: ColumnTypes[];
  data: any[];
  downlaodIcon?: boolean;
  viewIcon?: boolean;
  deleteIcon?: boolean;
  editIcon?: boolean;
  viewDetailsIcon?: boolean;
  activeIcon?: boolean;
  disabledActive?: boolean;
  disableInactive?: boolean;
  addIcon?: boolean;
  setLoading: (loading: boolean) => void;
  onEditNaviagte?: NavigateConfigTypes;
  onDeleteConfig?: ActionConfigTypes;
  onActiveConfig?: ActionConfigTypes;
  onDownloadConfig?: ActionConfigTypes;
  resendInviteConfig?: ActionConfigTypes;
  /** Enables the "activate immediately" option in the resend-invitation popup. */
  activateConfig?: ActivateAccountConfigTypes;
  from?: string;
  onViewConfig?: ActionConfigTypes;
  addConfig?: AddConfigTypes;
  onCustomerNameNaviagte?: NavigateConfigTypes;
  onCustomerNamePopUp?: (showModal: boolean, rowData: any) => void;
  getApiData?: (currentPage: number) => void;
  tenantId?: string;
  doSomethingOnParent?: (id: string) => void;
  profileLinkNaviagte?: NavigateConfigTypes;
  editModalHanlde?: (data: any) => void;
  manageIcon?: boolean;
  manageConfig?: ManageConfigTypes;
  oneRecordNotDelete?: boolean;
  invoiceIcon?: boolean;
  invoiceConfig?: any;
  orderIcon?: boolean;
  orderConfig?: InvoiceConfigTypes;
  checkboxDisable?: boolean;
  checkedIds?: string[];
  setCheckedIds?: (data: any) => void;
  resendSelectedInvoice?: (data: any) => void;
  downloadFileFromTable?: (data: any, type: string) => void;
  openServiceLinkModal?: boolean;
  setOpenServiceLinkModal?: (data: any) => void;
  setSelectedRowData?: (data: any) => void;
  table?: string;
  agentNameClickConfig?: {
    onClick: (data: any) => void;
  };
  setFilterApplied?: (data: any) => void;
  filterApplied?: boolean;
  onViewDetailsConfig?: any;
  onClickViewDetails?: () => {};
  prefillAgentName?: string;
  navigateToExternalTab?: boolean;
  approveIcon?: boolean;
  onApproveConfig?: { onClick: (row: any) => void; condition?: (row: any) => boolean };
  rejectIcon?: boolean;
  onRejectConfig?: { onClick: (row: any) => void; condition?: (row: any) => boolean };
  resendRequestIcon?: boolean;
  onResendRequestConfig?: { onClick: (row: any) => void; condition?: (row: any) => boolean };
  viewRejectionReasonIcon?: boolean;
  onViewRejectionReasonConfig?: { onClick: (row: any) => void; condition?: (row: any) => boolean };
  editIconLast?: boolean;
  onDeleteClick?: (row: any) => void;
}

interface ConfirmTextTypes {
  heading: string;
  para: string;
  extra: string;
}

const Table: React.FC<TablePropsTypes> = ({
  columns,
  data,
  downlaodIcon,
  viewIcon,
  deleteIcon,
  editIcon,
  activeIcon,
  addIcon,
  setLoading,
  onEditNaviagte,
  onDeleteConfig,
  onActiveConfig,
  onViewDetailsConfig,
  onDownloadConfig,
  resendInviteConfig,
  activateConfig,
  onViewConfig,
  addConfig,
  onCustomerNameNaviagte,
  onCustomerNamePopUp,
  getApiData,
  tenantId,
  doSomethingOnParent,
  profileLinkNaviagte,
  editModalHanlde,
  manageIcon,
  from,
  manageConfig,
  disabledActive,
  disableInactive,
  oneRecordNotDelete = false,
  invoiceIcon,
  orderIcon,
  orderConfig,
  checkboxDisable = false,
  checkedIds,
  setCheckedIds,
  resendSelectedInvoice,
  setOpenServiceLinkModal,
  openServiceLinkModal,
  setSelectedRowData,
  downloadFileFromTable,
  table,
  agentNameClickConfig,
  setFilterApplied,
  viewDetailsIcon,
  prefillAgentName,
  navigateToExternalTab,
  approveIcon,
  onApproveConfig,
  rejectIcon,
  onRejectConfig,
  resendRequestIcon,
  onResendRequestConfig,
  viewRejectionReasonIcon,
  onViewRejectionReasonConfig,
  editIconLast,
  onDeleteClick,
}) => {
  const { setMessage } = useMessage();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: string;
  } | null>(null);
  const [confirmModal, setConfirmModal] = useState<boolean>(false);
  const [reInvite, setReInvite] = useState<boolean>(false);
  const [activateModal, setActivateModal] = useState<boolean>(false);
  const [activating, setActivating] = useState<boolean>(false);
  const [confirmText, setConfirmText] = useState<ConfirmTextTypes>({
    heading: "",
    para: "",
    extra: "",
  });
  const [confirmSuccessText, setConfirmSuccessText] = useState<string>("");
  const [rowData, setRowData] = useState<any | null>(null);
  const [rowHandlerType, setRowHandlerType] = useState<string>("");
  const { currentPage } = useSelector((state: RootState) => state.pagination);
  // state to store row id for open dropdown of list
  const [openDropDownById, setOpenDropDownById] = useState<string>("");
  // To store the clicked config for the modal
  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const [currentConfig, setCurrentConfig] = useState<DropdownItemTypes | null>(
    null
  );
  const [selectAllChecked, setSelectAllChecked] = useState<boolean>(false);

  const sortedData = useMemo(() => {
    let sortableData = [...data];
    if (sortConfig !== null) {
      sortableData.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === "ascending" ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === "ascending" ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableData;
  }, [data, sortConfig]);

  const extractFileNameFromUrl = (url: string): string => {
    return url?.split("/")?.pop() ?? `Report.xlsx`;
  };
  const handleDownloadPdf = async (row: any, type?: string) => {
    if (row.invoiceNumber) {
      let data = {
        name: "Download Invoice",
        apiUrl: "",
        serialNumber: "id",
        downloadInvoice: {
          apiUrl: "/order/invoice/download/",
          fileName: "invoiceNumber",
        },
      };
      handleDropdownClick(data, row);
    } else if (row?.reportCat) {
      try {
        setLoading(true);
        const response = await getApi<{
          url?: string;
          status?: boolean;
          msg?: string;
        }>(`${GET_REPORT_URL}${row.id}`);
        if (!response?.success) {
          setMessage(
            response?.error?.message || GENERAL_ERROR_MESSAGE,
            "error"
          );
          return;
        }
        const pdfUrl = response?.data?.url;
        if (response?.data?.status || !pdfUrl) {
          setMessage(
            response?.data?.msg || REPORT_FILE_GENERATING_MESSAGE,
            "info"
          );
          return;
        }
        const fileName = extractFileNameFromUrl(pdfUrl);

        const link = document.createElement("a");
        link.href = pdfUrl;
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setMessage(REPORT_DOWNLOAD_SUCCESS, "success");
      } catch (error: any) {
        setMessage(
          error?.response?.data?.error?.message ||
          error?.message ||
          GENERAL_ERROR_MESSAGE,
          "error"
        );
      } finally {
        setLoading(false);
      }
    } else if (row?.downloadFile) {
      //Pass row data to parent component for download URL
      //Rest of the download logic is in parent component
      if (downloadFileFromTable && type) {
        downloadFileFromTable(row, type);
      }
    }
  };

  const requestSort = (key: string) => {
    let direction = "ascending";
    if (
      sortConfig &&
      sortConfig.key === key &&
      sortConfig.direction === "ascending"
    ) {
      direction = "descending";
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (key: string) => {
    if (!sortConfig) return <ArrowDownLightIcon />;
    return sortConfig.key === key ? (
      sortConfig.direction === "ascending" ? (
        <ArrowUpLightIcon />
      ) : (
        <ArrowDownLightIcon />
      )
    ) : (
      <ArrowDownLightIcon />
    );
  };

  // function to naviagte page
  const handleNavigatLink = (url: string, urlID: string) => {
    navigate(`${url}${urlID}`);
  };

  // function to naviagte page
  const handleNavigatLinkWithState = (
    url: string,
    urlID: string,
    data: any
  ) => {
    navigate(`${url}${urlID}`, { state: { data } });
    dispatch(setLocationData(data));
  };

  // function to store action buttons
  const handleAction = (
    row: any,
    actionType: string,
    confirmMsgheading: string,
    confirmMsgheadPara: string,
    successMsg: string,
    extraMsg: string
  ) => {
    setRowData(row);
    setRowHandlerType(actionType);
    setConfirmText({
      heading: confirmMsgheading,
      para: confirmMsgheadPara,
      extra: extraMsg,
    });
    setConfirmSuccessText(successMsg);
    setConfirmModal(true);
  };

  // function to set delete information
  const handleDelete = (row: any) => {
    handleAction(
      row,
      "Delete",
      onDeleteConfig?.heading || "Delete!",
      `delete ${onDeleteConfig?.rowName && row[onDeleteConfig?.rowName]
        ? onDeleteConfig?.rowName && row[onDeleteConfig?.rowName]
        : onDeleteConfig?.staticContent
      } ${(onDeleteConfig?.secondRowName && row[onDeleteConfig?.secondRowName]) ||
      ""
      }`,
      onDeleteConfig?.successMessage || "",
      ""
    );
  };

  // Add this after the handleDelete function (around line 396)

  // function to handle view details
  const handleViewDetails = (row: any) => {
    // Check if there's a custom onClick handler in the config
    if (onViewDetailsConfig?.onClickViewDetails) {
      // onViewDetailsConfig.onClick(row);
      onViewDetailsConfig?.onClickViewDetails(row);
      return;
    }
  };

  const handleLocationNavigate = (url: string, urlID: string) => {
    navigate(`${url}/${urlID}`);
  };

  // function to set active information
  const handleActiveUsers = async (row: any) => {
    if (
      !row?.isSignedUp &&
      !row.apiType &&
      !onActiveConfig?.notCheckedOnboarded
    ) {
      // setMessage(
      //   `${onActiveConfig?.onboardedMessage || ""} is not onboarded yet!`,
      //   "error"
      // );
      setReInvite(true);
      handleAction(
        row,
        "Resend",
        resendInviteConfig?.heading || "Delete!",
        `re-invite this ${onDeleteConfig?.rowName && row[onDeleteConfig?.rowName]
        } ${(onDeleteConfig?.secondRowName &&
          row[onDeleteConfig?.secondRowName]) ||
        ""
        }`,
        resendInviteConfig?.successMessage || "",
        resendInviteConfig?.extra || ""
      );
    } else {
      const action = row.active ? "Deactivate" : "Activate";
      const paraAction = row.active ? "deactivate" : "activate";
      handleAction(
        row,
        "Active",
        `${action} ${onActiveConfig?.heading}`,
        `${paraAction} the ${onActiveConfig?.rowName && row[onActiveConfig?.rowName]
        } ${(onActiveConfig?.secondRowName &&
          row[onActiveConfig?.secondRowName]) ||
        ""
        }`,
        `${onActiveConfig?.successMessage} ${row.active ? "deactivated" : "activated"
        } successfully!`,
        ""
      );
    }
  };

  // Swaps the resend-invitation popup for the activate popup. Only reachable
  // when the listing supplied an activateConfig. `reInvite` is left untouched so
  // the resend popup can be restored as-is on cancel.
  const handleOpenActivateModal = () => {
    setConfirmModal(false);
    setActivateModal(true);
  };

  // Cancel / X on the activate popup: closes only that popup and returns to the
  // resend-invitation popup it was launched from. No password is set and no
  // invitation is resent.
  const handleCancelActivate = () => {
    setActivateModal(false);
    setConfirmModal(true);
  };

  // Sets the password for a not-onboarded row and activates it. The popup stays
  // open on failure so the entered password is not lost.
  const handleActivateAccount = async (
    password: string,
    confirmPassword: string
  ) => {
    if (!activateConfig || !rowData) return;
    setActivating(true);
    try {
      const response = await putApi(
        `${activateConfig.apiUrl}${rowData[activateConfig.urlID]}`,
        {
          email: rowData[activateConfig.emailKey],
          password,
          confirmPassword,
        }
      );

      if (response.success) {
        setActivateModal(false);
        setReInvite(false);
        setMessage(activateConfig.successMessage, "success");
        getApiData && getApiData(currentPage);
      } else {
        setMessage(
          response.error?.message ?? activateConfig.errorMessage,
          "error"
        );
      }
    } catch (error) {
      setMessage(activateConfig.errorMessage, "error");
    } finally {
      setActivating(false);
    }
  };

  // function to hit api
  const handleConfirmAction = async () => {
    setReInvite(false);
    if (!rowData) return;
    setConfirmModal(false);
    setLoading(true);
    try {
      const configMap: { [key: string]: ActionConfigTypes | undefined } = {
        Delete: onDeleteConfig,
        Active: onActiveConfig,
        Add: addConfig,
        Resend: resendInviteConfig,
      };
      const currentConfig = configMap[rowHandlerType];
      if (!currentConfig) {
        setMessage(GENERAL_ERROR_MESSAGE, "error");
        setLoading(false);
        return;
      }

      let apiUrl = `${currentConfig.apiUrl}${rowData[currentConfig.urlID]}`;
      let payload: any = {};
      if (tenantId && rowData?.tenantId) {
        apiUrl = `${apiUrl}/${rowData?.tenantId}`;
      }
      if (currentConfig?.deleteByPayload) {
        apiUrl = `${currentConfig.apiUrl}`;
        currentConfig?.deleteByPayloadKeys &&
          currentConfig?.deleteByPayloadKeys.length &&
          currentConfig.deleteByPayloadKeys.map((item) => {
            payload[item] = rowData?.[item];
          });
      }
      if (currentConfig?.activeByPayload) {
        apiUrl = `${currentConfig.apiUrl}`;
        payload = {
          [currentConfig.activeByPayloadKeys || ""]:
            rowData?.[currentConfig.urlID],
          isActive: !rowData.active ? "true" : "false",
        };
      }
      if (currentConfig?.payloadKeys && currentConfig.payloadKeys?.length) {
        apiUrl = `${currentConfig.apiUrl}`;
        currentConfig.payloadKeys.map((item: string) => {
          payload[item] = rowData?.[item];
        });
      }
      if (currentConfig?.extendedParams) {
        apiUrl = apiUrl + "?";
        currentConfig?.extendedParams.map((item, index) => {
          const andMarkCheck = index === 0 ? "" : "&";
          apiUrl = apiUrl + `${andMarkCheck}${item}=${rowData[item] || ""}`;
        });
      }
      if (rowHandlerType === "Resend" && resendInviteConfig) {
        // Set the payload for Resend
        payload = {
          [resendInviteConfig?.urlID]: rowData[resendInviteConfig?.urlID],
        };
      }
      if (from) {
        payload["from"] = from;
      }

      const response =
        rowHandlerType === "Delete"
          ? currentConfig.deleteByPayload
            ? await deletePayloadApi(apiUrl, { data: payload })
            : currentConfig.deleteByPutApi
              ? await putApi(apiUrl, {})
              : await deleteApi(apiUrl)
          : rowHandlerType === "Add"
            ? await postApi(apiUrl, payload)
            : rowHandlerType === "Resend"
              ? await postApi(currentConfig.apiUrl, payload)
              : currentConfig.activeByPayload
                ? await activePayloadApi(apiUrl, payload)
                : await putApi(apiUrl, {
                  isActive: !rowData.active ? "true" : "false",
                  ...(rowData?.apiType && { onlyActiveDeactive: true }),
                  ...(currentConfig.activePayloadKeys?.reduce((acc: any, key: string) => {
                    acc[key] = rowData?.[key];
                    return acc;
                  }, {})),
                });

      if (response.success) {
        setMessage(confirmSuccessText, "success");
        if (rowHandlerType !== "Add") {
          getApiData && getApiData(currentPage);
        }

        // Redirect URL
        if (addConfig?.viewUrl) {
          handleNavigatLink(addConfig.viewUrl, "");
        }
      } else {
        setMessage(response.error?.message ?? GENERAL_ERROR_MESSAGE, "error");
      }
    } catch (error) {
      setMessage(GENERAL_ERROR_MESSAGE, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleAddAction = (row: any) => {
    handleAction(
      row,
      "Add",
      addConfig?.heading ? addConfig.heading : "",
      addConfig?.confirmationText ? addConfig.confirmationText : "",
      addConfig?.successMessage ? addConfig.successMessage : "",
      ""
    );
  };

  // Ref to handle clicks outside the dropdown to close it
  const dropdownRef = useOutsideClick<HTMLUListElement>(() => {
    setOpenDropDownById("");
  });

  // Handle dropdown click to open modal with the specific item config
  const handleDropdownClick = async (
    dropdownItem: DropdownItemTypes,
    rowData: any
  ) => {
    // A menu entry can opt into row-specific behaviour (different API, mapping
    // or layout) without duplicating the entry in the table config.
    const resolved: DropdownItemTypes = dropdownItem.resolveByRow
      ? { ...dropdownItem, ...dropdownItem.resolveByRow(rowData) }
      : dropdownItem;

    if (resolved.accordionFields || resolved.tableConfig) {
      setCurrentConfig(resolved); // Set only the clicked item's config for the modal
      setModalVisible(true); // Open the modal
    } else if (resolved.name === "Resend Invoice") {
      if (resendSelectedInvoice) {
        resendSelectedInvoice({
          invoiceId: rowData.id,
          invoiceNo: rowData.invoiceNumber,
          invoiceSetting: rowData.invoiceSetting,
        });
      }
    } else if (resolved.downloadInvoice) {
      try {
        setLoading(true);
        // Get serial number from the row data
        const serialNumber = rowData[resolved?.serialNumber];
        const fileName = `Invoice-Number-${rowData[resolved?.downloadInvoice?.fileName]
          }.pdf`;

        const response = await httpService.get(
          `/order/invoice/download/${serialNumber}`,
          {
            responseType: "blob", // Ensures the response is treated as binary (Blob)
          }
        );
        if (response) {
          const blob = response;
          // Detect Safari to use a different method
          const isSafari = /^((?!chrome|android).)*safari/i.test(
            navigator.userAgent
          );

          if (isSafari) {
            // Safari workaround using FileReader
            const reader = new FileReader();
            reader.onloadend = function () {
              const link = document.createElement("a");
              link.href = reader.result as string;
              link.download = fileName;
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
            };
            reader.readAsDataURL(blob); // Read blob as Data URL
          } else {
            // Standard method for other browsers
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = downloadUrl;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(downloadUrl); // Clean up the object URL
          }
          setMessage(TABLE_MESSAGES.INVOICE_DOWNLOAD_SUCCESS, "success");
        } else {
          setMessage(GENERAL_ERROR_MESSAGE, "error");
        }
      } catch (error) {
        console.error("Error downloading the invoice:", error);
        setMessage(GENERAL_ERROR_MESSAGE, "error");
      } finally {
        setLoading(false);
      }
    } else if (resolved.navigateItem) {
      const key = resolved.navigateItem.isCheckNavigateByKey;
      const apiUrl = resolved.apiUrl;
      const urlParamKey = resolved.navigateItem.urlParamKey;
      const urlParamKeyId = rowData?.[resolved.serialNumber];
      if (resolved.navigateItem?.isCheckNavigateByKey === "") {
        dispatch(setLocationData(rowData));
        navigate(`${resolved.navigateItem.path}`);
      } else {
        try {
          if (!apiUrl) {
            return;
          }
          setLoading(true);
          const response = await getApi(
            `${apiUrl}?${urlParamKey}=${urlParamKeyId}`
          );
          if (response.success && response.data) {
            const data: any = response.data;
            if (!data?.[key]) {
              setMessage(
                `${resolved.navigateItem?.showErrorMessage ||
                TABLE_MESSAGES.NO_FINALIZED_ORDER_FOUND
                }`,
                "error"
              );
            } else {
              dispatch(setLocationData(rowData));
              navigate(`${resolved.navigateItem.path}`);
            }
          } else {
            setMessage(
              response.error?.message ?? GENERAL_ERROR_MESSAGE,
              "error"
            );
          }
        } catch (error) {
          console.error("Error downloading the invoice:", error);
          setMessage(GENERAL_ERROR_MESSAGE, "error");
        } finally {
          setLoading(false);
        }
      }
    }
  };

  /**
   * Handles "Select All" checkbox change.
   * When clicked, either selects or deselects all rows.
   */
  const handleSelectAllChange = (
    newStatus: boolean,
    setHasChanged: boolean
  ) => {
    const newSelectAllChecked = newStatus;
    setSelectAllChecked(newSelectAllChecked);
    if (newSelectAllChecked) {
      const updatedData = data
        .filter((row) => !row?.checkboxDisabled)
        .map((row) => row.id);
      setCheckedIds && setCheckedIds(updatedData);

      // let updatedData = data.map((row) => row.id);
      // setCheckedIds && setCheckedIds(updatedData);
    } else {
      setCheckedIds && setCheckedIds([]);
    }

    /*
    setData(updatedData);
    if (setHasChanged) {
      setHasChanges && setHasChanges(true);
    }
    checkAmountChange && checkAmountChange(true);
    */
  };

  /**
   * Handles checkbox change for a row.
   * When checked, sets isModified to true, when unchecked, sets it to false.
   * @param rowIndex - Index of the row being modified.
   */
  const handleCheckboxChange = (rowId: string) => {
    setCheckedIds &&
      setCheckedIds((prevData: any) => {
        const newCheckedIds = prevData.includes(rowId)
          ? prevData.filter((item: string) => item !== rowId) // Remove item
          : [...prevData, rowId]; // Add item
        return newCheckedIds;
      });
  };
  // !Kept this code in case any regression occur because of this useEffect change for selecet all on table
  // useEffect(() => {
  //   if (checkedIds?.length && checkedIds?.length === data?.length) {
  //     setSelectAllChecked(true);
  //   } else {
  //     setSelectAllChecked(false);
  //   }
  // }, [checkedIds]);
  useEffect(() => {
    const enabledIds = data
      .filter((row) => !row?.checkboxDisabled)
      .map((row) => row.id);
    if (
      checkedIds?.length &&
      enabledIds.length &&
      checkedIds.every((id) => enabledIds.includes(id)) &&
      enabledIds.every((id) => checkedIds.includes(id))
    ) {
      setSelectAllChecked(true);
    } else {
      setSelectAllChecked(false);
    }
  }, [checkedIds, data]);

  return (
    <>
      <div className="tableContainer">
        <table className="table">
          <thead>
            <tr>
              {columns.map((column) => (
                <th key={column.key} style={column.columnStyle}>
                  {column.checkbox ? (
                    <input
                      type="checkbox"
                      checked={selectAllChecked}
                      onChange={() =>
                        handleSelectAllChange(!selectAllChecked, true)
                      }
                    // disabled={
                    //   data.every((row) => row.checkboxDisabled) ||
                    //   checkboxDisable
                    // }
                    />
                  ) : column.isSortable ? (
                    <div
                      className="sortingDiv"
                      onClick={() =>
                        column.isSortable && requestSort(column.key)
                      }
                    >
                      {column.header}
                      {column.isSortable && (
                        <span className="sortingDiv__icon">
                          {getSortIcon(column.key)}
                        </span>
                      )}
                    </div>
                  ) : column.isActive ? (
                    activeIcon && (
                      <div
                        className="sortingDiv sortingDiv--center"
                        onClick={() =>
                          column.isSortable && requestSort(column.key)
                        }
                      >
                        {column.header}
                        {column.isSortable && (
                          <span className="sortingDiv__icon">
                            {getSortIcon(column.key)}
                          </span>
                        )}
                      </div>
                    )
                  ) : column.colorPallete ? (
                    <div className="sortingDiv sortingDiv--center">
                      {column.header}
                    </div>
                  ) : column?.isDownloadIcon ? (
                    downlaodIcon && <GreenDownloadIcon />
                  ) : (
                    column.header
                  )}
                </th>
              ))}
              {(downlaodIcon || viewIcon || editIcon || deleteIcon || manageIcon || addIcon || invoiceIcon || orderIcon || viewDetailsIcon || approveIcon || rejectIcon || resendRequestIcon || viewRejectionReasonIcon) && (
                <th>Action</th>
              )}
            </tr>
          </thead>
          <tbody>
            {sortedData.length === 0 ? (
              <tr>
                <td colSpan={columns.length + 1}>
                  <div className="no-data floating">
                    {table === "OrderTrackingTable" ? "No Order Found!" : "No Results Found!"}
                  </div>
                </td>
              </tr>
            ) : (
              sortedData.map((row, index) => (
                <tr
                  key={index}
                  className={
                    row?.reviewOrderChangesCost === false ? "row-review" : ""
                  }
                >
                  {columns.map((column) => (
                    <td key={column.key} style={column.columnStyle}>
                      {column?.checkbox ? (
                        <input
                          type="checkbox"
                          checked={checkedIds && checkedIds.includes(row?.id)}
                          onChange={() => handleCheckboxChange(row?.id)}
                          disabled={row?.checkboxDisabled ?? checkboxDisable}
                        />
                      ) : column.isActive && activeIcon ? (
                        row[column.key] ? (
                          <div className="actions flex--center">
                            <button
                              className="btn p_0 border_0"
                              disabled={
                                (table === "userList" ||
                                  table === "customer" ||
                                  table === "customerUser") &&
                                row?.isEditable === false
                              }
                              onClick={() => {
                                if (
                                  !disabledActive &&
                                  !disableInactive &&
                                  row?.roles !== "Super Admin" &&
                                  !(
                                    table === "userList" &&
                                    row?.isEditable === false
                                  )
                                ) {
                                  if (column.handleActive) {
                                    handleActiveUsers(row);
                                  }
                                }
                              }}
                            >
                              {((table === "userList" ||
                                table === "customer" ||
                                table === "customerUser") &&
                                row?.isEditable === false) ||
                                row?.roles === "Super Admin" ? (
                                <DisabledActive />
                              ) : (
                                <ActiveIcon />
                              )}
                            </button>
                          </div>
                        ) : (
                          <div className="actions flex--center">
                            <button
                              className="btn p_0 border_0"
                              disabled={
                                (table === "userList" ||
                                  table === "customer" ||
                                  table === "customerUser") &&
                                row?.isEditable === false
                              }
                              onClick={() => {
                                if (
                                  !disabledActive &&
                                  row?.roles !== "Super Admin" &&
                                  !(
                                    (table === "userList" ||
                                      table === "customer" ||
                                      table === "customerUser") &&
                                    row?.isEditable === false
                                  )
                                ) {
                                  if (column.handleActive) {
                                    handleActiveUsers(row);
                                  }
                                }
                              }}
                            >
                              {((table === "userList" ||
                                table === "customer" ||
                                table === "customerUser") &&
                                row?.isEditable === false) ||
                                row?.roles === "Super Admin" ? (
                                <DisabledInactive />
                              ) : (
                                <UnactiveIcon />
                              )}
                            </button>
                          </div>
                        )
                      ) : column?.isDownloadIcon ? (
                        row[column.key] && (
                          <div className="actions flex--center">
                            <button
                              className="btn p_0 border_0"
                              onClick={() => handleDownloadPdf(row, column.key)}
                            >
                              <GreenDownloadIcon />
                            </button>
                          </div>
                        )
                      ) : column?.onCellClick ? (
                        <div className="cellText">
                          <span
                            className="cellText cellText--link"
                            role="button"
                            tabIndex={0}
                            style={{ cursor: "pointer" }}
                            onClick={() => column.onCellClick?.(row)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                column.onCellClick?.(row);
                              }
                            }}
                          >
                            {row[column.key] ?? column.header}
                          </span>
                        </div>
                      ) : column?.className === "link" ? (
                        <div className="profile">
                          <span
                            className="profile__name profile__name--underline"
                            onClick={() => {
                              if (onCustomerNameNaviagte) {
                                // onCustomerNameNaviagte(true);
                                sessionStorage.setItem(
                                  CUSTOMER_RATE_NAME,
                                  row?.customerName
                                );
                                handleNavigatLink(
                                  onCustomerNameNaviagte?.apiUrl,
                                  row?.[onCustomerNameNaviagte?.urlID]
                                );
                              }
                              if (onCustomerNamePopUp) {
                                onCustomerNamePopUp(true, row);
                              }
                              if (!openServiceLinkModal) {
                                //! not setting to prevent tab header loss
                                // localStorage.setItem(
                                //   AGENT_RATE_NAME,
                                //   row?.agentName
                                // );
                                dispatch(setLocationData(row));
                                setOpenServiceLinkModal?.(true);
                                setSelectedRowData?.(row);
                              }
                              if (handleDownloadPdf) {
                                handleDownloadPdf(row, column.key);
                              }
                            }}
                          >
                            {row[column.key]}
                            <NavigateIcon />
                          </span>
                        </div>
                      ) : column?.className === "profile" ? (
                        <div className="profile">
                          {row?.logo ? (
                            <div className="profile__image">
                              <img src={row?.logo} alt={row[column.key]?.[0]} />
                            </div>
                          ) : (
                            <div className="profile__initials uppercase">
                              {row[column.key]?.[0]}
                            </div>
                          )}
                          <span className="profile__name">
                            {row[column.key]}
                          </span>
                        </div>
                      ) : column?.className === "simpleLink" ? (
                        <div
                          className="cellText"
                          style={{ display: "flex", alignItems: "center", gap: "4px" }}
                        >
                          <span
                            className="cellText__primary cellText__primary--link"
                            style={{ cursor: "pointer", textDecoration: "underline" }}
                            onClick={() => {
                              if (profileLinkNaviagte) {
                                handleNavigatLinkWithState(
                                  profileLinkNaviagte?.apiUrl,
                                  row?.[profileLinkNaviagte?.urlID],
                                  row
                                );
                              }
                            }}
                          >
                            {row[column.key]}
                          </span>
                          {column?.showExternalLink && profileLinkNaviagte && (
                            <button
                              type="button"
                              className="btn p_0 border_0 externalLinkBtn"
                              title="Open in new tab"
                              style={{ display: "inline-flex", flexShrink: 0 }}
                              onClick={(e) => {
                                e.stopPropagation();
                                const url = `${profileLinkNaviagte?.apiUrl}${row?.[profileLinkNaviagte?.urlID]}`;
                                window.open(url, "_blank");
                              }}
                            >
                              <ExternalLink />
                            </button>
                          )}
                        </div>
                      ) : column?.className === "profileWithNavigate" ? (
                        <div
                          className="profile flex-center"
                          onClick={() => {
                            if (profileLinkNaviagte) {

                              sessionStorage.setItem(
                                AGENT_RATE_NAME,
                                row?.agentName
                              );
                              handleNavigatLinkWithState(
                                profileLinkNaviagte?.apiUrl,
                                row?.[profileLinkNaviagte?.urlID],
                                row
                              );
                            }
                          }}
                        >
                          {row?.logo ? (
                            <div className="profile__image">
                              <img src={row?.logo} alt={row[column.key]?.[0]} />
                            </div>
                          ) : (
                            <div className="profile__initials uppercase">
                              {row[column.key]?.[0]}
                            </div>
                          )}
                          <span
                            className="profile__name"
                            style={{ textDecoration: "underline" }}
                          >
                            {row[column.key]}
                          </span>
                        </div>
                      ) : // location Link in the Integrations
                        // location Link in the Integrations
                        column?.className === "locationLink" ? (
                          row[column.key] ? (
                            <button
                              onClick={() => {
                                if (onCustomerNameNaviagte) {
                                  // Check if there's a custom onClick handler
                                  if (onCustomerNameNaviagte.onClick) {
                                    onCustomerNameNaviagte.onClick(row);
                                    // } else {
                                    //   // Original navigation logic
                                    //   const customerName =
                                    //     row?.name ||
                                    //     localStorage.getItem("CUSTOMER_RATE_NAME") ||
                                    //     "";
                                    //   if (customerName) {
                                    //     localStorage.setItem("CUSTOMER_RATE_NAME", customerName);
                                    //   }
                                    // handleLocationNavigate(
                                    //   onCustomerNameNaviagte?.apiUrl,
                                    //   onCustomerNameNaviagte?.urlID
                                    // );
                                  }
                                }
                              }}
                              className="btn p_0 border_0 link link__blue"
                            >
                              {row[column.key]}
                            </button>
                          ) : (
                            <div className="cellText">
                              <span className="cellText__dark">-</span>
                            </div>
                          )
                        ) : column?.className === "profileLink" ? (
                          <div
                            className="profile"
                            onClick={() => {
                              if (agentNameClickConfig?.onClick) {
                                agentNameClickConfig.onClick(row);
                              }
                              if (profileLinkNaviagte) {

                                sessionStorage.setItem(
                                  AGENT_RATE_NAME,
                                  row?.agentName
                                );
                                handleNavigatLinkWithState(
                                  profileLinkNaviagte?.apiUrl,
                                  row?.[profileLinkNaviagte?.urlID],
                                  row
                                );
                              }
                            }}
                          >
                            <div className="profile__initials uppercase">
                              {row[column.key]?.[0]}
                            </div>
                            {column?.subKey && row[column.subKey] ? (
                              <span className="profile__stack">
                                <span
                                  className="profile__name"
                                  style={{ textDecoration: "underline" }}
                                >
                                  {row[column.key]}
                                </span>
                                {/* Reference only — the parent cell navigates,
                                    so swallow the click here to keep the DC
                                    order number non-interactive. */}
                                <span
                                  className="profile__subText"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  {row[column.subKey]}
                                </span>
                              </span>
                            ) : (
                              <span
                                className="profile__name"
                                style={{ textDecoration: "underline" }}
                              >
                                {row[column.key]}
                              </span>
                            )}
                            {navigateToExternalTab && profileLinkNaviagte && (
                              <button
                                className="btn p_0 border_0 externalLinkBtn"
                                title="Open in new tab"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const url = `${profileLinkNaviagte?.apiUrl}${row?.[profileLinkNaviagte?.urlID]}`;
                                  window.open(url, "_blank");
                                }}
                              >
                                <ExternalLink />
                              </button>
                            )}
                          </div>
                        ) : column?.className === "count" ? (
                          <div
                            className="cellText"
                            style={{ width: "max-content" }}
                          >
                            <span className="cellText__dark cellText__dark--gray">
                              {row[column.key]}
                            </span>
                          </div>
                        ) : column?.className === "lastName" ? (
                          <div className="cellText">
                            <span className="cellText__primary">
                              {row[column.key]}
                            </span>
                          </div>
                        ) : column?.className === "addNote" ? (
                          <span
                            className="profile__name profile__name--underline"
                            onClick={() =>
                              doSomethingOnParent && doSomethingOnParent(row?.id)
                            }
                          >
                            {`${row[column.key] == "" ? "+Add Note" : "Edit Note"
                              }`}
                          </span>
                        ) : column?.upperCase ? (
                          <div className="cellText">
                            <span className="cellText__dark capitalize">
                              {row[column.key]}
                              {column.externalLinkConfig && (
                                <button
                                  type="button"
                                  className="btn p_0 border_0 externalLinkBtn"
                                  title="Open in new tab"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    window.open(column.externalLinkConfig!.getUrl(row), "_blank");
                                  }}
                                >
                                  <ExternalLink />
                                </button>
                              )}
                            </span>
                          </div>
                        ) : column?.dolloar ? (
                          <div className="cellText">
                            <span className="cellText__dark capitalize">
                              ${Number(row[column.key])?.toFixed(2)}
                            </span>
                          </div>
                        ) : column?.className === "requestStatus" ? (
                          <div className="cellText">
                            <span className={`requestStatusBadge requestStatusBadge--${(row[column.key] || "").toLowerCase()}`}>
                              {row[column.key]}
                            </span>
                          </div>
                        ) : column?.className === "chips" ? (
                          <ChipsCell
                            items={row[column.key]}
                            header={column.header}
                            maxChips={column.maxChips}
                          />
                        ) : column?.className === "commissionBadge" ? (
                          row[column.key] ? (
                            <div className="cellText">
                              <span
                                className={`commissionBadge commissionBadge--${(
                                  row[column.key] || ""
                                )
                                  .toLowerCase()
                                  .replace(/[^a-z0-9]/g, "")}`}
                              >
                                {row[column.key]}
                              </span>
                            </div>
                          ) : (
                            <div className="cellText">
                              <span className="cellText__dark">--</span>
                            </div>
                          )
                        ) : column?.colorPallete ? (
                          <div className={`cellText cellText--center`}>
                            <span
                              className="cellText__colorPicker"
                              style={{ background: row[column.key] }}
                            ></span>
                          </div>
                        ) : column?.isHoverable ? (
                          <>
                            <div
                              className="cellText"
                              data-tip={row[column.key]}
                              data-for={`tooltip-${column.key}-${row.id}`}
                              title={row?.tooltip}
                            >
                              <span className="cellText__dark">
                                {row[column.key]}
                              </span>
                            </div>
                          </>
                        ) : (
                          <div className="cellText">
                            <span className="cellText__dark">
                              {row[column.key]}
                            </span>
                          </div>
                        )}
                    </td>
                  ))}
                  {downlaodIcon ||
                    viewIcon ||
                    editIcon ||
                    deleteIcon ||
                    manageIcon ||
                    addIcon ||
                    invoiceIcon ||
                    orderIcon ||
                    viewDetailsIcon ||
                    approveIcon ||
                    rejectIcon ||
                    resendRequestIcon ||
                    viewRejectionReasonIcon ? (
                    <td>
                      <div className="actions flex--end">
                        {downlaodIcon && (
                          <button className="btn p_0 border_0">
                            <GreenDownloadIcon
                              onClick={() => handleDownloadPdf(row)}
                            />
                          </button>
                        )}
                        {viewDetailsIcon && (
                          <button
                            onClick={() => handleViewDetails(row)}
                            className={`btn p_0 border_0 link ${onViewDetailsConfig?.linkClassName ?? "link__blue"}`}
                          >
                            {onViewDetailsConfig?.heading}
                            {!onViewDetailsConfig?.hideExternalIcon && <ExternalLink />}
                          </button>
                        )}
                        {viewIcon && (
                          <button
                            className="btn p_0 border_0"
                            onClick={() => {
                              if (onViewConfig) {
                                if (tenantId) {
                                  handleNavigatLink(
                                    onViewConfig?.apiUrl,
                                    `${row?.[onViewConfig?.urlID]}/${row?.tenantId
                                    }`
                                  );
                                } else if (onViewConfig?.changeStateToView) {
                                  onViewConfig?.changeStateToView(2, {
                                    [onViewConfig?.urlID]:
                                      row[onViewConfig?.urlID],
                                    [onViewConfig?.tenantIdUrl || ""]:
                                      row?.[onViewConfig?.tenantIdUrl || ""],
                                  });
                                } else {
                                  handleNavigatLink(
                                    onViewConfig?.apiUrl,
                                    row?.[onViewConfig?.urlID]
                                  );
                                }
                              }
                            }}
                          >
                            <BlueViewIcon />
                          </button>
                        )}
                        {editIcon && !editIconLast && (!onEditNaviagte?.condition || onEditNaviagte.condition(row)) && (
                          <button
                            disabled={
                              row?.roles === "Super Admin" ||
                                ((table === "userList" ||
                                  table === "customer" ||
                                  table === "customerUser") &&
                                  row?.isEditable === false)
                                ? true
                                : row?.disableEditIcon
                            }
                            title="Edit"
                            className="btn p_0 border_0"
                            onClick={() => {
                              if (onEditNaviagte) {

                                if (row?.agentName && onEditNaviagte?.apiUrl === '/agent-api/edit/') {
                                  sessionStorage.setItem(
                                    AGENT_RATE_NAME,
                                    row?.agentName
                                  )
                                }
                                if (onEditNaviagte.isEditWithoutLink && onEditNaviagte.onClick) {
                                  onEditNaviagte.onClick(row);
                                }
                                else if (tenantId) {
                                  handleNavigatLink(
                                    onEditNaviagte?.apiUrl,
                                    `${row?.[onEditNaviagte?.urlID]}/${row?.tenantId
                                    }`
                                  );
                                } else if (onEditNaviagte?.stateData) {
                                  handleNavigatLinkWithState(
                                    onEditNaviagte?.apiUrl,
                                    row?.[onEditNaviagte?.urlID],
                                    row
                                  );
                                } else if (onEditNaviagte?.changeStateToEdit) {
                                  onEditNaviagte?.changeStateToEdit(2, {
                                    [onEditNaviagte?.urlID]:
                                      row[onEditNaviagte?.urlID],
                                    serviceId: row?.serviceId || "",
                                  });
                                } else {
                                  handleNavigatLink(
                                    onEditNaviagte?.apiUrl,
                                    row?.[onEditNaviagte?.urlID]
                                  );
                                }
                              } else if (editModalHanlde) {
                                editModalHanlde(row);
                              }
                            }}
                          >
                            <EditUserIcon />
                          </button>
                        )}
                        {deleteIcon && (
                          <button
                            className="btn p_0 border_0"
                            onClick={() => onDeleteClick ? onDeleteClick(row) : handleDelete(row)}
                            // Rows may opt out of deletion individually via
                            // `deleteDisabled`, the same way `checkboxDisabled` works.
                            disabled={
                              !!row?.deleteDisabled ||
                              !!(
                                oneRecordNotDelete &&
                                row?.parentCustomerId &&
                                data.filter((a) => a.id)?.length === 1
                              )
                            }
                          >
                            <DeleteIcon />
                          </button>
                        )}
                        {invoiceIcon && <OrangeFileIcon />}
                        {orderIcon && (
                          <button
                            className="btn p_0 border_0"
                            onClick={() => {
                              if (
                                orderConfig &&
                                orderConfig?.changeStateToEdit
                              ) {
                                orderConfig?.changeStateToEdit(row);
                              }
                            }}
                          >
                            <BlueFileIcon />
                          </button>
                        )}
                        {addIcon &&
                          (row?.alreadyAdded ? (
                            <button
                              type="button"
                              className="btn btn__primary"
                              onClick={() => {
                                addConfig &&
                                  // handleNavigatLink(
                                  //   addConfig && addConfig?.viewUrl,
                                  //   row?.[addConfig?.viewUrlID]
                                  // );
                                  handleNavigatLinkWithState(
                                    addConfig?.viewUrl,
                                    row?.[addConfig?.viewUrlID],
                                    row
                                  );
                              }}
                              disabled={prefillAgentName ? true : false}
                            >
                              {prefillAgentName ? "Added" : "View"}
                            </button>
                          ) : (
                            <button
                              type="button"
                              className="btn btn__primary"
                              onClick={() => {
                                addConfig && handleAddAction(row);
                              }}
                            >
                              + Add
                            </button>
                          ))}
                        {approveIcon && (!onApproveConfig?.condition || onApproveConfig.condition(row)) && (
                          <button
                            className="btn p_0 border_0"
                            title="Approve"
                            onClick={() => onApproveConfig?.onClick(row)}
                          >
                            <ApproveIcon />
                          </button>
                        )}
                        {rejectIcon && (!onRejectConfig?.condition || onRejectConfig.condition(row)) && (
                          <button
                            className="btn p_0 border_0"
                            title="Reject"
                            onClick={() => onRejectConfig?.onClick(row)}
                          >
                            <RejectIcon />
                          </button>
                        )}
                        {resendRequestIcon && (!onResendRequestConfig?.condition || onResendRequestConfig.condition(row)) && (
                          <button
                            className="btn p_0 border_0"
                            title="Resend Request"
                            onClick={() => onResendRequestConfig?.onClick(row)}
                          >
                            <ResendIcon />
                          </button>
                        )}
                        {viewRejectionReasonIcon && (!onViewRejectionReasonConfig?.condition || onViewRejectionReasonConfig.condition(row)) && (
                          <button
                            className="btn p_0 border_0"
                            title="View Rejection Reason"
                            onClick={() => onViewRejectionReasonConfig?.onClick(row)}
                          >
                            <BlueFileIcon />
                          </button>
                        )}
                        {editIcon && editIconLast && (!onEditNaviagte?.condition || onEditNaviagte.condition(row)) && (
                          <button
                            disabled={
                              row?.roles === "Super Admin" ||
                                ((table === "userList" ||
                                  table === "customer" ||
                                  table === "customerUser") &&
                                  row?.isEditable === false)
                                ? true
                                : row?.disableEditIcon
                            }
                            title="Edit"
                            className="btn p_0 border_0"
                            onClick={() => {
                              if (onEditNaviagte) {
                                if (row?.agentName && onEditNaviagte?.apiUrl === '/agent-api/edit/') {
                                  sessionStorage.setItem(AGENT_RATE_NAME, row?.agentName);
                                }
                                if (onEditNaviagte.isEditWithoutLink && onEditNaviagte.onClick) {
                                  onEditNaviagte.onClick(row);
                                } else if (tenantId) {
                                  handleNavigatLink(
                                    onEditNaviagte?.apiUrl,
                                    `${row?.[onEditNaviagte?.urlID]}/${row?.tenantId}`
                                  );
                                } else if (onEditNaviagte?.stateData) {
                                  handleNavigatLinkWithState(
                                    onEditNaviagte?.apiUrl,
                                    row?.[onEditNaviagte?.urlID],
                                    row
                                  );
                                } else if (onEditNaviagte?.changeStateToEdit) {
                                  onEditNaviagte?.changeStateToEdit(2, {
                                    [onEditNaviagte?.urlID]: row[onEditNaviagte?.urlID],
                                    serviceId: row?.serviceId || "",
                                  });
                                } else {
                                  handleNavigatLink(
                                    onEditNaviagte?.apiUrl,
                                    row?.[onEditNaviagte?.urlID]
                                  );
                                }
                              } else if (editModalHanlde) {
                                editModalHanlde(row);
                              }
                            }}
                          >
                            <EditUserIcon />
                          </button>
                        )}
                        {manageIcon && (
                          <ul className="dropdown">
                            <li className="dropdown__item">
                              <button
                                className="btn p_0 border_0"
                                onClick={() => {
                                  // Open dropdown only for the current row
                                  setOpenDropDownById(
                                    openDropDownById === row?.id ? "" : row?.id
                                  );
                                }}
                              >
                                <ThreeDotIcon />
                              </button>
                              <ul
                                className={`dropdownMenu dropdownMenu${openDropDownById === row?.id ? "--open" : ""
                                  }`}
                                ref={
                                  openDropDownById === row?.id
                                    ? dropdownRef
                                    : null
                                }
                              >
                                {manageConfig?.dropdownName?.map(
                                  (item, dropdownIndex: number) => (
                                    <li
                                      className="dropdown__item"
                                      key={`dropdown-${dropdownIndex}`}
                                      onClick={() =>
                                        handleDropdownClick(item, row)
                                      }
                                    >
                                      <span className="icon">{item?.icon}</span>
                                      {item?.name}
                                    </li>
                                  )
                                )}
                                {/* {manageConfig?.dropdownName?.map(
                                  (item, dropdownIndex: number) => {
                                    if (
                                      item.name === "Resend Invoice" &&
                                      row?.checkboxDisabled
                                    ) {
                                      return null; // ❌ Do not render this dropdown item
                                    }
                                    return (
                                      <li
                                        className="dropdown__item"
                                        key={`dropdown-${dropdownIndex}`}
                                        onClick={() =>
                                          handleDropdownClick(item, row)
                                        }
                                      >
                                        <span className="icon">
                                          {item?.icon}
                                        </span>
                                        {item?.name}
                                      </li>
                                    );
                                  }
                                )} */}
                              </ul>
                            </li>
                          </ul>
                        )}
                      </div>
                    </td>
                  ) : (
                    ""
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {confirmModal && (
        <ConfirmModal
          confirmInfo={confirmText}
          onConfirm={handleConfirmAction}
          setConfirmModal={setConfirmModal}
          reInvite={reInvite}
          setReInvite={setReInvite}
          activateInfo={
            activateConfig &&
              rowHandlerType === "Resend" &&
              rowData?.[activateConfig.emailKey]
              ? {
                message: activateConfig.noteMessage,
                onActivate: handleOpenActivateModal,
              }
              : undefined
          }
        />
      )}
      {activateModal && activateConfig && (
        <ActivateAccountModal
          title={activateConfig.title}
          email={rowData?.[activateConfig.emailKey] || ""}
          saving={activating}
          onCancel={handleCancelActivate}
          onSave={handleActivateAccount}
        />
      )}
      {modalVisible && currentConfig && (
        <DropdownModal
          onClose={() => setModalVisible(false)}
          config={currentConfig}
          id={openDropDownById}
        />
      )}
    </>
  );
};

export default Table;
